"use client";

import Link from "next/link";
import { Calendar, ChevronLeft, Clock4, Tag } from "lucide-react";
import ImageComponent from "@core/helpers/client/components/image";
import { alt_unless_repeated } from "@/helpers/imageAlt";

const to_padded_jalali = (timestamp) => {
    const ms = parseInt(timestamp);
    if (!ms) return "";

    const parts = new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date(ms));

    const get = (type) => parts.find((part) => part.type === type)?.value || "";
    return `${get("year")}/${get("month")}/${get("day")}`;
};

/* کلاس‌های کارت عمودی (پیش‌فرض) و حالت `horizontal_mobile`. در حالت دوم زیر md
   کارت یک ردیف لیستی است (تصویر راست، متن و دسته کنارش، ردیف تاریخ تمام‌عرض
   زیرش) و از md به بعد دقیقا همان کارت عمودی می‌شود. در موبایل گرید روی ریشه
   است و wrapperها `contents` می‌شوند تا ساختار DOM برای هر دو حالت یکی بماند. */
const CLASSES = {
    vertical: {
        root: "hover-shadow-full-light flex h-full flex-col overflow-hidden rounded-md border border-jmt-neutral-200 bg-jmt-white",
        main_link: "flex flex-col",
        image: "relative h-[135px] lg:h-[170px] w-full shrink-0 overflow-hidden bg-jmt-neutral-100",
        text: "flex flex-col gap-3 px-3 pt-3",
        bottom: "mt-auto flex flex-col gap-3 px-3 pb-3 pt-2",
        category: "",
        footer: "",
    },
    horizontal_mobile: {
        root: "grid grid-cols-[92px_minmax(0,1fr)] content-start gap-x-3 gap-y-2 bg-jmt-white border-b border-jmt-neutral-200 pb-4 max-md:last:border-b-0 max-md:last:pb-0 md:flex md:h-full md:flex-col md:gap-0 md:overflow-hidden md:rounded-md md:border md:pb-0 md:hover:shadow-[0_2px_6px_rgba(0,0,0,0.06),0_6px_16px_rgba(0,0,0,0.08)]",
        main_link: "contents md:flex md:flex-col",
        image: "relative row-span-2 size-[92px] shrink-0 overflow-hidden rounded-md bg-jmt-neutral-100 md:h-[135px] md:w-full md:rounded-none lg:h-[170px]",
        text: "flex flex-col gap-2 md:gap-3 md:px-3 md:pt-3",
        bottom: "contents md:mt-auto md:flex md:flex-col md:gap-3 md:px-3 md:pb-3 md:pt-2",
        category: "self-end md:self-auto",
        footer: "col-span-2",
    },
};

// heading_level: در صفحه‌های فهرست (جستجو، دسته‌ها) کارت‌ها مستقیم زیر h1 صفحه‌اند و h3 یک سطح را می‌پرید
// (Lighthouse: heading-order)؛ آن‌ها "h2" می‌دهند. زیر بخش‌های h2 (خانه، ویدیوها، اسلایدر مقاله) همان h3.
// image_priority: سطح بارگذاری تصویر ("lcp" | "eager" | ...)؛ پیش‌فرض همان context صفحه (lazy). فهرست‌ها کارت‌های
// ردیف اول را بی‌درنگ می‌خواهند: در دسته‌های سلر عکس کارت اول LCP موبایل بود ولی lazy لود می‌شد.
const AcademyCard = ({ blog, storage_prefix_url, horizontal_mobile = false, heading_level = "h3", image_priority }) => {
    const Heading = heading_level === "h2" ? "h2" : "h3";
    const href = blog?.seo?.full_url || "#";
    const date = to_padded_jalali(blog?.publishAt || blog?.createdAt);
    const category = blog?.category;
    const category_href = category?.seo?.full_url;
    const cls = horizontal_mobile ? CLASSES.horizontal_mobile : CLASSES.vertical;

    return (
        <div className={cls.root}>

            <Link href={href} className={cls.main_link}>
                <div className={cls.image}>
                    <ImageComponent
                        storage_prefix_url={storage_prefix_url}
                        src={blog?.media?.url || ""}
                        // تصویر داخل همان لینکی است که عنوان مقاله را دارد؛ alt برابر عنوان دو بار خوانده می‌شد
                        // (image-redundant-alt)، پس در آن حالت خالی است
                        alt={alt_unless_repeated(blog?.media?.alt || blog?.title, blog?.title)}
                        width={400}
                        height={170}
                        priority_level={image_priority}
                        style={{ width: "100%", height: "100%" }}
                        image_className="w-full h-full object-cover"
                    />
                </div>

                <div className={cls.text}>
                    <Heading className="line-clamp-2 text-sm font-bold text-jmt-main">
                        {blog?.title}
                    </Heading>

                    {blog?.summary && (
                        <p className="line-clamp-2 text-[13px] text-jmt-neutral-600">
                            {blog?.summary}
                        </p>
                    )}
                </div>
            </Link>

            <div className={cls.bottom}>
                {category?.title && (
                    <Link
                        href={category_href || "#"}
                        className={`flex w-fit items-center gap-1 rounded-xl bg-jmt-neutral-100 px-2 py-1 text-xs font-bold text-jmt-neutral-600 transition hover:text-jmt-main ${cls.category}`}>

                        {category?.title}
                    </Link>
                )}

                <Link
                    href={href}
                    className={`flex items-center justify-between gap-2 text-xs text-jmt-neutral-600 ${cls.footer}`}>
                    <span className="flex items-center gap-2">
                        {blog?.reading_time ? (
                            <>
                                <span className="flex items-center gap-1 whitespace-nowrap">
                                    <Clock4 size={16} />
                                    زمان مطالعه {blog?.reading_time}′
                                </span>
                                <span className="text-jmt-neutral-300">|</span>
                            </>
                        ) : null}

                        {date && (
                            <span className="flex items-center gap-1 whitespace-nowrap">
                                <Calendar size={16} />
                                {date}
                            </span>
                        )}
                    </span>

                    <ChevronLeft size={16} className="shrink-0 text-jmt-neutral-900" />
                </Link>
            </div>
        </div>
    );
};

export default AcademyCard;
